import {
  ACTION_WINDOW_DAYS,
  daysUntilDue,
} from "@/lib/actionAlerts/dueDate";

export type WorkflowDeadlineTone = "default" | "warning" | "muted";

export function formatWorkflowDeadline(
  value: string | null,
  emptyLabel = "No deadline",
): string {
  if (!value) return emptyLabel;
  // Render in UTC so the label shows the same calendar day that was entered,
  // regardless of the viewer's timezone. Due dates are a date-only concept
  // stored as midnight-UTC timestamps; formatting in local time shifted the
  // label back a day for viewers west of UTC. This also keeps the absolute
  // label aligned with daysUntilDue, which already counts UTC day-numbers.
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function workflowDeadlineSummary(
  value: string | null,
  options: { emptyLabel?: string } = {},
): string {
  const emptyLabel = options.emptyLabel ?? "No deadline set";
  if (!value) return emptyLabel;
  const days = daysUntilDue(value);
  if (days === null) return emptyLabel;
  const formatted = formatWorkflowDeadline(value);
  if (days < 0) {
    return `${formatted} · Passed ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
  }
  if (days === 0) return `${formatted} · Due today`;
  if (days === 1) return `${formatted} · Due tomorrow`;
  if (days <= ACTION_WINDOW_DAYS) return `${formatted} · ${days} days remaining`;
  return formatted;
}

export function workflowDeadlineTone(value: string | null): WorkflowDeadlineTone {
  if (!value) return "muted";
  const days = daysUntilDue(value);
  if (days === null) return "muted";
  if (days < 0 || days <= ACTION_WINDOW_DAYS) return "warning";
  return "default";
}

export function isWorkflowOverdue(value: string | null): boolean {
  if (!value) return false;
  const days = daysUntilDue(value);
  return days !== null && days < 0;
}

export function sortByWorkflowDeadline<T extends { deadline_at: string | null }>(
  items: T[],
): T[] {
  return [...items].sort((a, b) => {
    if (!a.deadline_at && !b.deadline_at) return 0;
    if (!a.deadline_at) return 1;
    if (!b.deadline_at) return -1;
    return new Date(a.deadline_at).getTime() - new Date(b.deadline_at).getTime();
  });
}
